import React, { useState, useEffect } from "react";
import { X, Globe, Plus, Edit2, Trash2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { MenuLink } from "../../modules/shared/model/menu-link.interfaces";

// Supported languages (excluding English)
const SUPPORTED_LANGUAGES = [
  { code: "it", name: "Italiano" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "pt", name: "Português" },
  { code: "ru", name: "Русский" },
  { code: "zh", name: "中文" },
  { code: "ja", name: "日本語" },
  { code: "ar", name: "العربية" },
  { code: "hi", name: "हिन्दी" },
  { code: "ko", name: "한국어" },
  { code: "nl", name: "Nederlands" },
  { code: "cs", name: "Čeština" },
];

interface MenuLinkTranslationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  menuLink: MenuLink | null;
  onSave: (translations: Record<string, string>) => Promise<void>;
}

const MenuLinkTranslationDialog: React.FC<MenuLinkTranslationDialogProps> = ({
  isOpen,
  onClose,
  menuLink,
  onSave,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [translatedTitle, setTranslatedTitle] = useState<string>("");
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [editingLanguage, setEditingLanguage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (menuLink?.translatedTitle) {
      setTranslations(menuLink.translatedTitle);
    } else {
      setTranslations({});
    }
  }, [menuLink]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedLanguage("");
      setTranslatedTitle("");
      setEditingLanguage(null);
      setError("");
    }
  }, [isOpen]);

  const handleAddTranslation = () => {
    if (!selectedLanguage) {
      setError("Please select a language");
      return;
    }

    if (!translatedTitle.trim()) {
      setError("Please enter a translated title");
      return;
    }

    const updatedTranslations = {
      ...translations,
      [selectedLanguage]: translatedTitle,
    };

    setTranslations(updatedTranslations);
    setSelectedLanguage("");
    setTranslatedTitle("");
    setError("");
  };

  const handleEditTranslation = (langCode: string) => {
    setEditingLanguage(langCode);
    setSelectedLanguage(langCode);
    setTranslatedTitle(translations[langCode]);
  };

  const handleUpdateTranslation = () => {
    if (!editingLanguage) return;

    if (!translatedTitle.trim()) {
      setError("Please enter a translated title");
      return;
    }

    const updatedTranslations = {
      ...translations,
      [editingLanguage]: translatedTitle,
    };

    setTranslations(updatedTranslations);
    setEditingLanguage(null);
    setSelectedLanguage("");
    setTranslatedTitle("");
    setError("");
  };

  const handleDeleteTranslation = (langCode: string) => {
    const { [langCode]: _, ...rest } = translations;
    setTranslations(rest);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      await onSave(translations);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save translations");
    } finally {
      setSaving(false);
    }
  };

  const getLanguageName = (langCode: string) => {
    const language = SUPPORTED_LANGUAGES.find(lang => lang.code === langCode);
    return language?.name || langCode;
  };

  if (!isOpen || !menuLink) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Manage Menu Link Translations
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  Original: {menuLink.title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Add/Edit Translation Form */}
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                {editingLanguage ? (
                  <>
                    <Edit2 className="h-4 w-4 text-blue-600" />
                    Edit Translation
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 text-blue-600" />
                    Add New Translation
                  </>
                )}
              </h3>

              <div className="space-y-4">
                {/* Language Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => {
                      setSelectedLanguage(e.target.value);
                      setError("");
                    }}
                    disabled={editingLanguage !== null}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Language</option>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <option
                        key={lang.code}
                        value={lang.code}
                        disabled={translations[lang.code] && !editingLanguage}
                      >
                        {lang.name} ({lang.code})
                        {translations[lang.code] && !editingLanguage ? " - Already added" : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Translated Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Translated Title
                  </label>
                  <Input
                    value={translatedTitle}
                    onChange={(e) => {
                      setTranslatedTitle(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter translated menu link title"
                    className="w-full"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {editingLanguage ? (
                    <>
                      <Button
                        onClick={handleUpdateTranslation}
                        className="flex-1"
                        size="sm"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Update Translation
                      </Button>
                      <Button
                        onClick={() => {
                          setEditingLanguage(null);
                          setSelectedLanguage("");
                          setTranslatedTitle("");
                          setError("");
                        }}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleAddTranslation}
                      className="flex-1"
                      size="sm"
                      disabled={!selectedLanguage || !translatedTitle}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Translation
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            {/* Existing Translations */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Current Translations ({Object.keys(translations).length})
                </h3>
              </div>

              {Object.keys(translations).length === 0 ? (
                <Card className="p-8 text-center">
                  <Globe className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">
                    No translations added yet. Add your first translation above.
                  </p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {Object.entries(translations).map(([langCode, title]) => (
                    <Card
                      key={langCode}
                      className="p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {getLanguageName(langCode)}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-900 font-medium break-words">
                            {title}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditTranslation(langCode)}
                            disabled={editingLanguage !== null}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Edit translation"
                          >
                            <Edit2 className="h-4 w-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDeleteTranslation(langCode)}
                            disabled={editingLanguage !== null}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete translation"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              onClick={onClose}
              variant="outline"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="min-w-[100px]"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MenuLinkTranslationDialog;

